import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface ChartFrameProps {
  children: ReactNode;
  className?: string;
}

export function ChartFrame({ children, className }: ChartFrameProps) {
  return (
    <div
      className={cn(
        "h-72 w-full text-xs",
        "[&_.recharts-cartesian-axis-tick-value]:fill-muted-foreground",
        "[&_.recharts-cartesian-grid-line]:stroke-border/70",
        "[&_.recharts-legend-item-text]:text-muted-foreground",
        className,
      )}
    >
      {children}
    </div>
  );
}

type ChartTooltipPayload = {
  name?: ReactNode;
  dataKey?: string | number;
  value?: string | number | null;
  color?: string;
  payload?: Record<string, unknown>;
};

export interface ChartTooltipContentProps {
  active?: boolean;
  payload?: ChartTooltipPayload[];
  label?: ReactNode;
  labelFormatter?: (label: ReactNode) => ReactNode;
  valueFormatter?: (
    value: string | number | null | undefined,
    item: ChartTooltipPayload,
  ) => ReactNode;
}

export function ChartTooltipContent({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
}: ChartTooltipContentProps) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="min-w-36 rounded-lg border bg-background/95 p-2.5 text-xs shadow-md backdrop-blur">
      {label ? (
        <div className="mb-2 font-medium text-foreground">
          {labelFormatter ? labelFormatter(label) : label}
        </div>
      ) : null}
      <div className="space-y-1.5">
        {payload.map((item) => {
          const itemKey = String(item.dataKey ?? item.name ?? "value");
          return (
            <div key={itemKey} className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color ?? "currentColor" }}
                />
                <span className="truncate">{item.name ?? itemKey}</span>
              </div>
              <span className="font-mono tabular-nums text-foreground">
                {valueFormatter ? valueFormatter(item.value, item) : item.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
